import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AppModule } from '@overseer/contracts';
import { Logo } from '@/components/ui/logo';
import { MODULES } from '@/lib/modules';
import { useChoosePreference } from '@/lib/preferences';

/**
 * Choose an application, on the way in.
 *
 * Shown only to a user with **more than one** module and no remembered choice.
 * One-module users never see it: they are redirected straight through, which is
 * the whole point of the single-module case.
 *
 * Choosing sets the default, so this appears once and then not again. It stays
 * reachable through the switcher, which is where somebody goes to change their
 * mind — a screen that reappears on every sign-in would be a toll booth.
 */
export const ModulePicker = ({ modules }: { modules: AppModule[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remember = useChoosePreference();

  const choose = (module: AppModule) => {
    remember.defaultModule(module);
    void navigate({ to: MODULES[module].to as never });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <Logo className="size-8 text-brand" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">{t('app.name')}</h1>
            <p className="text-sm text-fg-muted">{t('modules.pickPrompt')}</p>
          </div>
        </div>

        <ul className="space-y-2">
          {modules.map((module) => {
            const definition = MODULES[module];

            return (
              <li key={module}>
                <button
                  type="button"
                  onClick={() => choose(module)}
                  className="flex w-full items-start gap-3 rounded-lg border border-border-default bg-bg-raised p-4 text-left hover:border-brand hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-ring"
                >
                  <definition.icon aria-hidden className="mt-0.5 size-5 shrink-0 text-brand" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{t(definition.labelKey)}</span>
                    <span className="block text-xs text-fg-muted">
                      {t(definition.descriptionKey)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Said once, here, because the choice is remembered per browser and a
            person on a second machine will meet this screen again and wonder
            whether something went wrong. */}
        <p className="mt-4 text-xs text-fg-muted">{t('modules.pickRemembered')}</p>
      </div>
    </div>
  );
};

/**
 * A signed-in account with no application.
 *
 * A real state, not a defensive branch: it is what a newly invited user sees
 * before an administrator assigns them a module group. It **must say so**.
 *
 * The failure this replaces is the one CLAUDE.md records — a user with no
 * `department_N` group saw a working application with nothing in it, and had no
 * way to tell "you have no access" from "there is no data". An empty module list
 * would render the same way if this screen did not exist.
 */
export const NoModules = ({ onSignOut }: { onSignOut: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md text-center">
        <Logo className="mx-auto mb-4 size-8 text-fg-muted" />
        <h1 className="text-lg font-semibold tracking-tight">{t('modules.noneTitle')}</h1>
        <p className="mt-2 text-sm text-fg-muted">{t('modules.noneBody')}</p>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-6 text-sm text-accent underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {t('auth.signOut')}
        </button>
      </div>
    </div>
  );
};
